/**
 * PDF Text Layer Extractor
 * 
 * Extracts text directly from PDF text layer (for PDFs with selectable text).
 * Much more accurate than OCR for digital PDFs.
 */

import * as pdfjs from 'pdfjs-dist';

// Use local worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/**
 * Extract text from a specific region of a PDF page
 * @param {File|Blob|ArrayBuffer} pdfSource - PDF file
 * @param {number} pageNumber - Page number (1-indexed)
 * @param {Object} region - Region coordinates as percentages {x, y, width, height}
 * @returns {Promise<Object>} - Extracted text and structure
 */
export async function extractTextFromRegion(pdfSource, pageNumber = 1, region = null) {
  try {
    console.log('📄 PDF Text Extractor starting...');
    console.log('  - Source type:', pdfSource?.constructor?.name);
    console.log('  - Page:', pageNumber);
    console.log('  - Region:', region);
    
    // Load the PDF
    let data;
    if (pdfSource instanceof File || pdfSource instanceof Blob) {
      data = await pdfSource.arrayBuffer();
      console.log('  - ArrayBuffer size:', data.byteLength);
    } else {
      data = pdfSource;
    }
    
    const pdf = await pdfjs.getDocument({ data }).promise;
    console.log('  - PDF loaded, pages:', pdf.numPages);
    
    const page = await pdf.getPage(pageNumber);
    
    // Get text content
    const textContent = await page.getTextContent();
    const viewport = page.getViewport({ scale: 1.0 });
    
    console.log('  - Viewport:', viewport.width, 'x', viewport.height);
    console.log('  - Text items found:', textContent.items.length);
    
    // Filter items by region if specified
    let items = textContent.items;
    
    if (region && region.width > 0 && region.height > 0) {
      const regionBounds = {
        left: (region.x / 100) * viewport.width,
        top: (region.y / 100) * viewport.height,
        right: ((region.x + region.width) / 100) * viewport.width,
        bottom: ((region.y + region.height) / 100) * viewport.height
      };
      
      console.log('  - Region (%):', region);
      console.log('  - Region bounds (px):', regionBounds);
      
      // Log sample text item positions for debugging
      if (textContent.items.length > 0) {
        const sampleItems = textContent.items.slice(0, 5);
        console.log('  - Sample item positions:');
        sampleItems.forEach((item, i) => {
          const x = item.transform[4];
          const y = viewport.height - item.transform[5];
          console.log(`    ${i}: "${item.str.substring(0, 20)}" at (${x.toFixed(0)}, ${y.toFixed(0)})`);
        });
      }
      
      items = items.filter(item => {
        const transform = item.transform;
        const x = transform[4];
        const y = viewport.height - transform[5]; // PDF coordinates are bottom-up
        
        return x >= regionBounds.left && 
               x <= regionBounds.right && 
               y >= regionBounds.top && 
               y <= regionBounds.bottom;
      });
      
      console.log('  - Items after region filter:', items.length);
      
      // If no items found, try without region filter and warn
      if (items.length === 0) {
        console.log('  ⚠️ No items in region. Trying full page extraction...');
        items = textContent.items;
      }
    }
    
    // Group items by line (similar Y position)
    const lines = groupItemsByLine(items, viewport);
    
    // Detect columns based on X positions
    const columns = detectColumns(items);
    
    // Build structured table data
    const tableData = buildTableFromLines(lines, columns);
    
    // Also get raw text
    const rawText = items.map(item => item.str).join(' ');
    
    return {
      success: true,
      rawText,
      lines: lines.map(line => line.map(item => item.str).join(' ')),
      tableData,
      columns: columns.length,
      confidence: 95 // Text layer extraction is very reliable
    };
    
  } catch (error) {
    console.error('PDF text extraction failed:', error);
    return {
      success: false,
      error: error.message,
      rawText: '',
      lines: [],
      tableData: { headers: [], rows: [] }
    };
  }
}

/**
 * Group text items by line based on Y position
 */
function groupItemsByLine(items, viewport, tolerance = 5) {
  if (items.length === 0) return [];
  
  // Sort by Y position (top to bottom)
  const sorted = [...items].sort((a, b) => {
    const yA = viewport.height - a.transform[5];
    const yB = viewport.height - b.transform[5];
    return yA - yB;
  });
  
  const lines = [];
  let currentLine = [sorted[0]];
  let currentY = viewport.height - sorted[0].transform[5];
  
  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i];
    const itemY = viewport.height - item.transform[5];
    
    if (Math.abs(itemY - currentY) <= tolerance) {
      currentLine.push(item);
    } else {
      // Sort current line by X position (left to right)
      currentLine.sort((a, b) => a.transform[4] - b.transform[4]);
      lines.push(currentLine);
      currentLine = [item];
      currentY = itemY;
    }
  }
  
  // Don't forget the last line
  if (currentLine.length > 0) {
    currentLine.sort((a, b) => a.transform[4] - b.transform[4]);
    lines.push(currentLine);
  }
  
  return lines;
}

/**
 * Detect column boundaries from text items
 */
function detectColumns(items, minGap = 20) {
  if (items.length === 0) return [];
  
  // Collect all X positions
  const xPositions = items.map(item => item.transform[4]);
  const sortedX = [...new Set(xPositions)].sort((a, b) => a - b);
  
  // Find gaps that indicate column boundaries
  const columns = [sortedX[0]];
  
  for (let i = 1; i < sortedX.length; i++) {
    const gap = sortedX[i] - sortedX[i - 1];
    if (gap > minGap) {
      columns.push(sortedX[i]);
    }
  }
  
  return columns;
}

/**
 * Build table structure from lines and columns
 */
function buildTableFromLines(lines, columns) {
  if (lines.length === 0) return { headers: [], rows: [] };
  
  const rows = lines.map(lineItems => {
    const row = new Array(columns.length).fill('');
    
    lineItems.forEach(item => {
      const x = item.transform[4];
      // Find which column this item belongs to
      let colIndex = columns.length - 1;
      for (let i = 0; i < columns.length; i++) {
        if (i === columns.length - 1 || x < columns[i + 1]) {
          colIndex = i;
          break;
        }
      }
      row[colIndex] = (row[colIndex] + ' ' + item.str).trim();
    });
    
    return row;
  });
  
  // First row might be headers
  const headers = rows.length > 0 ? rows[0] : [];
  const dataRows = rows.slice(1);
  
  return {
    headers,
    rows: dataRows
  };
}

/**
 * Extract all text from a PDF page (no region filter)
 */
export async function extractFullPageText(pdfSource, pageNumber = 1) {
  return extractTextFromRegion(pdfSource, pageNumber, null);
}

/**
 * Get page count from PDF
 */
export async function getPdfPageCount(pdfSource) {
  try {
    let data;
    if (pdfSource instanceof File || pdfSource instanceof Blob) {
      data = await pdfSource.arrayBuffer();
    } else {
      data = pdfSource;
    }
    
    const pdf = await pdfjs.getDocument({ data }).promise;
    return pdf.numPages;
  } catch (error) {
    console.error('Failed to get page count:', error);
    return 0;
  }
}

const pdfTextExtractor = {
  extractTextFromRegion,
  extractFullPageText,
  getPdfPageCount
};

export default pdfTextExtractor;
